import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, useAuth } from '@halo/web-lib';
import logoLight from '../assets/logo/icon-text-logo-light.svg';

const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyEmailPage() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [cooldown, setCooldown] = useState(0);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verifyMutation = useMutation({
    mutationFn: async (verificationToken: string) => {
      const res = await api.post('/api/v1/auth/verify-email', { token: verificationToken });
      if (!res.success) {
        throw new Error(res.error ?? 'Email verification failed');
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/api/v1/auth/send-verification');
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to send verification email');
      }
    },
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });

  // Auto-verify on mount when a valid token is present
  const hasTriggered = useRef(false);
  useEffect(() => {
    if (token && !hasTriggered.current) {
      hasTriggered.current = true;
      verifyMutation.mutate(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = useCallback(() => {
    if (resendMutation.isPending || cooldown > 0) return;
    resendMutation.mutate();
  }, [resendMutation, cooldown]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="flex justify-center mb-8">
          <img src={logoLight} alt="Halo" className="h-8" />
        </div>

        <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl border border-gray-800 p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-bold text-white">Email Verification</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Confirm your email address</p>

          {/* No token — invalid link */}
          {!token ? (
            <>
              <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
                Invalid verification link.
              </div>
              {isAuthenticated && (
                <AdminResendButton
                  onResend={handleResend}
                  isPending={resendMutation.isPending}
                  cooldown={cooldown}
                  isSuccess={resendMutation.isSuccess}
                  error={resendMutation.error}
                />
              )}
            </>
          ) : verifyMutation.isPending ? (
            /* Loading state */
            <div className="flex items-center gap-3 px-3 py-3 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
              <span className="text-sm text-gray-300">
                Verifying your email...
              </span>
            </div>
          ) : verifyMutation.isSuccess ? (
            /* Success state */
            <>
              <div className="px-3 py-2 mb-4 text-sm text-green-400 bg-green-950/50 border border-green-900 rounded-lg">
                Email verified! Your account is now fully activated.
              </div>
              <Link
                to="/"
                className="block w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors text-center"
              >
                Go to dashboard
              </Link>
            </>
          ) : (
            /* Error state */
            <>
              <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
                {verifyMutation.error?.message ?? 'Verification failed. The link may be expired or invalid.'}
              </div>
              {isAuthenticated && (
                <AdminResendButton
                  onResend={handleResend}
                  isPending={resendMutation.isPending}
                  cooldown={cooldown}
                  isSuccess={resendMutation.isSuccess}
                  error={resendMutation.error}
                />
              )}
            </>
          )}

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-6">
          Halo Admin Panel &middot; Authorized personnel only
        </p>
      </div>
    </div>
  );
}

function AdminResendButton({
  onResend,
  isPending,
  cooldown,
  isSuccess,
  error,
}: {
  readonly onResend: () => void;
  readonly isPending: boolean;
  readonly cooldown: number;
  readonly isSuccess: boolean;
  readonly error: Error | null;
}) {
  return (
    <div className="space-y-3">
      {isSuccess && cooldown > 0 && (
        <div className="px-3 py-2 text-sm text-green-400 bg-green-950/50 border border-green-900 rounded-lg">
          Verification email sent! Check your inbox.
        </div>
      )}
      {error && (
        <div className="px-3 py-2 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
          {error.message}
        </div>
      )}
      <button
        type="button"
        onClick={onResend}
        disabled={isPending || cooldown > 0}
        className="w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {isPending
          ? 'Sending...'
          : cooldown > 0
            ? `Resend verification email (${cooldown}s)`
            : 'Resend verification email'}
      </button>
    </div>
  );
}
